import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sparkles } from 'lucide-react';
import { useAppStore } from '@/shared/store/appStore';
import { apiClient } from '@/shared/api/client';
import { getImageUrl } from '@/shared/utils/imageUrl';

const DEFAULT_PRIMARY_HERO = {
  id: '1',
  label: 'Cinema Hardware',
  title: 'TECHNOLOGY, WITHIN REACH',
  subtitle: 'Cinescope India connects filmmakers, production houses, and rental facilities across India with leading cinema camera support, anamorphic optics, stabilizers, wireless, and power solutions.',
  specs: [
    { label: 'SUPPORT', value: 'PAN-INDIA' },
    { label: 'CALIBRATION', value: 'METROLOGY' },
    { label: 'DISTRIBUTION', value: 'DIRECT' }
  ],
  colorPrimary: [0.47, 0.23, 0.98],
  colorAccent: [0.96, 0.97, 0.98],
  colorHex: '#7b39fc',
  primaryCTA: 'Explore Store',
  primaryCTALink: '/shop',
  secondaryCTA: 'Contact Support',
  secondaryCTALink: '#contact-anchor',
  videoUrl: '',
  imageUrl: getImageUrl('/static/uploads/hero-tabs/f8fb17a70d154146b8f68bc7d29be327.webp')
};

export default function Hero() {
  const { demoMode } = useAppStore();
  const [domains, setDomains] = useState([DEFAULT_PRIMARY_HERO]);
  const [activeDomain, setActiveDomain] = useState(DEFAULT_PRIMARY_HERO);
  const [loadedImages, setLoadedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  // Never show a loading spinner — we always have DEFAULT_PRIMARY_HERO to render immediately.
  // Showing a skeleton then replacing it causes CLS 0.179. We silently update state from the API.
  const [loading] = useState(false);

  // Check if current image is cached in browser
  useEffect(() => {
    if (activeDomain?.imageUrl && !loadedImages[activeDomain.imageUrl] && !failedImages[activeDomain.imageUrl]) {
      const img = new Image();
      img.src = activeDomain.imageUrl;
      if (img.complete && img.naturalWidth > 0) {
        setLoadedImages((prev) => ({ ...prev, [activeDomain.imageUrl]: true }));
      }
    }
  }, [activeDomain?.imageUrl, loadedImages, failedImages]);

  // Helper to map database tab format to client domain format
  const mapTabToDomain = (tab) => {
    const videoUrl = tab.video_url ? (tab.video_url.startsWith('http') ? tab.video_url : `${apiClient.defaults.baseURL || ''}${tab.video_url}`) : '';
    const imageUrl = tab.image_url ? getImageUrl(tab.image_url) : '';

    return {
      id: tab.id.toString(),
      label: tab.tab_name,
      title: tab.title,
      subtitle: tab.subtitle,
      specs: tab.specs || [],
      colorPrimary: [0.47, 0.23, 0.98],
      colorAccent: [0.96, 0.97, 0.98],
      colorHex: '#7b39fc',
      primaryCTA: tab.primary_cta || 'Explore Store',
      primaryCTALink: tab.primary_cta_link || '/shop',
      secondaryCTA: tab.secondary_cta || 'Contact Support',
      secondaryCTALink: tab.secondary_cta_link || '#contact-anchor',
      videoUrl: videoUrl,
      imageUrl: imageUrl || DEFAULT_PRIMARY_HERO.imageUrl
    };
  };

  // Auto-advance loop for domains (5 seconds interval)
  useEffect(() => {
    if (domains.length <= 1) return;
    const interval = setInterval(() => {
      setActiveDomain((prev) => {
        if (!prev) return domains[0];
        const currentIndex = domains.findIndex((d) => d.id === prev.id);
        if (currentIndex === -1) return domains[0];
        const nextIndex = (currentIndex + 1) % domains.length;
        return domains[nextIndex];
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [domains]);

  // Silently fetch hero-tabs from API and update state — no loading spinner to avoid CLS.
  // DEFAULT_PRIMARY_HERO is always rendered on first paint; API content replaces it silently.
  useEffect(() => {
    let mounted = true;

    apiClient.get('/api/v1/landing/hero-tabs')
      .then((response) => {
        if (!mounted) return;
        if (response.data && response.data.length > 0) {
          const mapped = response.data.map(mapTabToDomain);
          setDomains(mapped);
          setActiveDomain(mapped[0]);
        }
        // On empty response: keep DEFAULT_PRIMARY_HERO — no layout change
      })
      .catch((error) => {
        console.warn("Failed to fetch landing hero tabs from API:", error);
        // On error: keep DEFAULT_PRIMARY_HERO — no layout change
      });

    return () => {
      mounted = false;
    };
  }, [demoMode]);

  // Smooth scroll helper for anchor links
  const handleNavigation = (e, link) => {
    if (link.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(link);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Staggered container animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] // Custom cubic-bezier
      }
    }
  };

  const isCurrentImgLoaded = !activeDomain?.imageUrl || !!loadedImages[activeDomain.imageUrl];

  return (
    <section className="relative w-full min-h-screen flex flex-col justify-between overflow-hidden py-12 md:py-16 px-4 sm:px-8 md:px-12 select-none -mt-[80px] md:-mt-[90px] pt-[95px] sm:pt-[105px] md:pt-[115px] bg-black text-white">
      
      {/* Background Media: Neutral cinema dark gradient fallback, rendered strictly with Admin uploaded media */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b0914] via-[#05040a] to-black z-0 pointer-events-none" />

      {/* Shimmer skeleton layer while background image is loading */}
      {activeDomain?.imageUrl && !activeDomain?.videoUrl && (
        <div
          className={`absolute inset-0 z-0 overflow-hidden pointer-events-none transition-opacity duration-700 ${
            isCurrentImgLoaded ? 'opacity-0' : 'opacity-100'
          }`}
          aria-hidden="true"
        >
          {/* Base dark backdrop */}
          <div className="absolute inset-0 bg-[#0c0a18]" />
          {/* Glowing Animated Shimmer Wave */}
          <div className="absolute inset-0 animate-shimmer-wave" />
          {/* Subtle cinematic gradient vignette */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/80" />
        </div>
      )}

      {activeDomain?.videoUrl ? (
        <video
          key={activeDomain.id + activeDomain.videoUrl}
          className="absolute inset-0 w-full h-full object-cover object-top z-0 hidden md:block"
          autoPlay
          loop
          muted
          playsInline
          src={activeDomain.videoUrl}
        />
      ) : activeDomain?.imageUrl && !failedImages[activeDomain.imageUrl] ? (
        <img
          key={activeDomain.id + activeDomain.imageUrl}
          className={`absolute inset-0 w-full h-full object-cover object-top z-0 transition-opacity duration-700 ease-out ${
            isCurrentImgLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          src={activeDomain.imageUrl}
          alt=""
          role="presentation"
          fetchPriority="high"
          width="1200"
          height="588"
          decoding="async"
          onLoad={() => {
            setLoadedImages((prev) => ({ ...prev, [activeDomain.imageUrl]: true }));
          }}
          onError={() => {
            setFailedImages((prev) => ({ ...prev, [activeDomain.imageUrl]: true }));
            setLoadedImages((prev) => ({ ...prev, [activeDomain.imageUrl]: true }));
          }}
        />
      ) : null}

      {/* Main Centered Content Grid */}
      <div className="relative z-10 max-w-4xl w-full mx-auto flex flex-col items-center text-center my-auto px-2 sm:px-4 py-2 sm:py-6">
        {loading || !activeDomain ? (
          <div className="flex flex-col items-center space-y-6 text-center w-full animate-pulse">
            <div className="h-16 md:h-24 w-3/4 max-w-2xl bg-white/10 rounded-2xl" />
            <div className="h-6 w-1/2 max-w-md bg-white/10 rounded-xl" />
            <div className="h-20 w-full max-w-xl bg-white/5 rounded-2xl" />
            <div className="h-12 w-48 bg-white/10 rounded-xl mt-4" />
          </div>
        ) : (
          <div 
            key={activeDomain.id}
            className="flex flex-col items-center space-y-4 sm:space-y-6 text-center w-full animate-fadeIn"
          >
            {/* Headline (Instrument Serif font, italic white, responsive scaling) */}
            <h1 
              className="font-heading italic text-white text-3xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-normal leading-[1.08] sm:leading-[1.05] tracking-[-1px] sm:tracking-[-3px] max-w-4xl"
            >
              {activeDomain.title}
            </h1>

            {/* Subtitle */}
            <p 
              className="font-inter font-normal text-xs sm:text-base md:text-lg text-white/80 sm:text-white/70 max-w-2xl leading-relaxed font-light mt-1 sm:mt-2 px-2 sm:px-0"
            >
              {activeDomain.subtitle}
            </p>

            {/* Specifications Micro-Grid (Technical Depth, fully responsive for mobile and desktop) */}
            {activeDomain.specs && activeDomain.specs.length > 0 && (
              <div 
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 p-4 sm:p-5 backdrop-blur-[12px] rounded-2xl border border-white/10 bg-black/50 sm:bg-black/40 max-w-xl w-full mt-4 sm:mt-6 text-left shadow-2xl divide-y divide-white/10 sm:divide-y-0"
              >
                {activeDomain.specs.map((spec, index) => (
                  <div 
                    key={index} 
                    className="pt-2.5 sm:pt-0 first:pt-0 sm:border-r sm:last:border-none sm:pr-4 sm:border-white/10 space-y-1"
                  >
                    <span className="block text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-[#a78bfa] sm:text-white/50 font-bold">
                      {spec.label}
                    </span>
                    <span className="block text-xs sm:text-xs md:text-sm font-semibold sm:font-display sm:font-extrabold uppercase text-white tracking-wide leading-relaxed sm:leading-snug">
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* CTA Action Buttons Row (Full width on mobile, inline on desktop) */}
            <div 
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full max-w-md pt-3 sm:pt-4"
            >
              {activeDomain.primaryCTALink.startsWith('#') ? (
                <a
                  href={activeDomain.primaryCTALink}
                  onClick={(e) => handleNavigation(e, activeDomain.primaryCTALink)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[12px] text-white font-cabin text-sm font-bold uppercase tracking-wider transition-all hover:scale-[1.02] shadow-[0_4px_14px_rgba(123,57,252,0.35)] active:scale-98 bg-[#7b39fc] hover:bg-[#8d52ff] cursor-pointer"
                >
                  {activeDomain.primaryCTA}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={activeDomain.primaryCTALink}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[12px] text-white font-cabin text-sm font-bold uppercase tracking-wider transition-all hover:scale-[1.02] shadow-[0_4px_14px_rgba(123,57,252,0.35)] active:scale-98 bg-[#7b39fc] hover:bg-[#8d52ff] cursor-pointer"
                >
                  {activeDomain.primaryCTA}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}

              {activeDomain.secondaryCTALink.startsWith('#') ? (
                <a
                  href={activeDomain.secondaryCTALink}
                  onClick={(e) => handleNavigation(e, activeDomain.secondaryCTALink)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[12px] backdrop-blur-sm font-cabin text-sm font-bold uppercase tracking-wider transition-all hover:scale-[1.02] active:scale-98 shadow-md border border-white/15 bg-[#2b2344]/90 hover:bg-[#3d325f] text-[#f6f7f9] cursor-pointer"
                >
                  {/* <Sparkles className="w-4 h-4 text-[#a78bfa]" /> */}
                  {activeDomain.secondaryCTA}
                </a>
              ) : (
                <Link
                  to={activeDomain.secondaryCTALink}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[12px] backdrop-blur-sm font-cabin text-sm font-bold uppercase tracking-wider transition-all hover:scale-[1.02] active:scale-98 shadow-md border border-white/15 bg-[#2b2344]/90 hover:bg-[#3d325f] text-[#f6f7f9] cursor-pointer"
                >
                  <Play className="w-3.5 h-3.5 fill-current text-[#a78bfa]" />
                  {activeDomain.secondaryCTA}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Horizontal Domain Navigation Bar */}
      {domains.length > 1 && (
        <div className="relative z-10 max-w-3xl w-full mx-auto mt-6 sm:mt-12 backdrop-blur-[8px] border border-white/10 p-1 sm:p-1.5 rounded-full bg-black/40 shadow-xl overflow-x-auto no-scrollbar">
          <div role="tablist" aria-label="Cinema equipment domains" className="flex justify-between items-center relative w-full min-w-max sm:min-w-0">
            {domains.map((domain) => (
              <button
                key={domain.id}
                type="button"
                role="tab"
                aria-label={`Select domain ${domain.label}`}
                aria-selected={activeDomain?.id === domain.id}
                onClick={() => setActiveDomain(domain)}
                className={`relative flex-1 text-center px-4 py-2 sm:py-2.5 text-[9px] sm:text-[10px] font-mono tracking-widest font-bold transition-colors duration-300 rounded-full whitespace-nowrap cursor-pointer ${
                  activeDomain?.id === domain.id ? 'text-white z-10' : 'text-white/50 hover:text-white'
                }`}
              >
                {/* Sliding dynamic selection capsule */}
                {activeDomain?.id === domain.id && (
                  <div
                    className="absolute inset-0 border border-white/10 rounded-full z-[-1] bg-white/10 shadow-sm"
                  />
                )}
                {domain.label}
              </button>
            ))}
          </div>
        </div>
      )}

    </section>
  );
}
